import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** @type {Record<string, string>} */
const SHIPPING_CURRENCY_SYMBOLS = {
  PLN: "zł",
  EUR: "€",
  USD: "$",
  GBP: "£",
  CZK: "Kč",
};

/** @type {Record<string, string>} */
const SHIPPING_LOCALE_MAP = {
  pl: "pl-PL",
  en: "en-US",
  de: "de-DE",
  fr: "fr-FR",
  cs: "cs-CZ",
};

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {TextComponent} [cartShipping] - The shipping estimate value.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );

    this.#updateShipping();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty(
      "--row-height",
      `${cartItemRowToRemove.clientHeight}px`,
    );
    cartItemRowToRemove.classList.add("removing");

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();
    this.refs.cartShipping?.shimmer();
    /** @type {TextComponent | null} */ (
      this.querySelector(".at-free-shipping-progress text-component")
    )?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html",
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          }),
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
        );
        this.#updateShipping(
          parsedResponseText.total_weight,
          parsedResponseText.total_price,
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      this.#disableCartItems();
      this.refs.cartTotal?.shimmer();
      this.refs.cartShipping?.shimmer();
      /** @type {TextComponent | null} */ (
        this.querySelector(".at-free-shipping-progress text-component")
      )?.shimmer();

      sectionRenderer
        .renderSection(this.sectionId, { cache: false })
        .then(() => {
          resetShimmer(this);
          this.#updateShipping();
        })
        .finally(() => {
          this.#enableCartItems();
        });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateShipping();
    } else {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false })
        .then(() => this.#updateShipping());
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Parses shipping rates JSON from a data attribute.
   * @param {string | undefined} raw
   * @returns {any[]}
   */
  #parseShippingRates(raw) {
    try {
      const parsed = JSON.parse(raw || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * Updates the shipping estimate value.
   * @param {number} [totalWeight] - Total weight in grams from cart API response.
   * @param {number} [totalPrice] - Total price in cents from cart API response.
   */
  #updateShipping(totalWeight, totalPrice) {
    const ratesEl = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-shipping-rates]")
    );
    if (!ratesEl) return;

    const cartShipping = ratesEl.querySelector("text-component");
    if (!cartShipping) return;

    const setShippingValue = (formatted) => {
      cartShipping.textContent = formatted;
      cartShipping.setAttribute("value", formatted);
      cartShipping.removeAttribute("shimmer");
    };

    const freeLabel = ratesEl.dataset.freeLabel || "";
    if (ratesEl.dataset.country !== "PL") {
      setShippingValue(freeLabel);
      return;
    }

    if (totalPrice == null) {
      totalPrice = parseInt(ratesEl.dataset.totalPrice || "0", 10) || 0;
    }

    const thresholdEnabled = ratesEl.dataset.freeShippingEnabled === "true";
    const threshold = parseInt(ratesEl.dataset.threshold || "0", 10);
    if (thresholdEnabled && threshold > 0 && totalPrice >= threshold) {
      setShippingValue(freeLabel);
      return;
    }

    if (totalWeight == null) {
      totalWeight = parseInt(ratesEl.dataset.totalWeight || "0", 10) || 0;
    }

    const rates = this.#parseShippingRates(ratesEl.dataset.shippingRates);
    rates.sort(
      /** @param {any} a @param {any} b */ (a, b) => {
        const aMax = this.#rateMaxWeight(a);
        const bMax = this.#rateMaxWeight(b);
        const aHasMax = aMax && aMax.value != null ? 0 : 1;
        const bHasMax = bMax && bMax.value != null ? 0 : 1;
        return aHasMax - bHasMax;
      },
    );
    const matched = rates.find(
      /** @param {any} r */ (r) => {
        const minG = this.#weightToGrams(this.#rateMinWeight(r), 0);
        const maxG = this.#weightToGrams(this.#rateMaxWeight(r), Infinity);
        return totalWeight >= minG && totalWeight <= maxG;
      },
    );

    const price = matched ? this.#ratePrice(matched) : null;
    if (price?.amount != null) {
      setShippingValue(
        this.#formatShippingPrice(
          parseFloat(price.amount),
          price.currency_code,
        ),
      );
    } else {
      setShippingValue("—");
    }
  }

  /**
   * @param {any} rate
   * @returns {{ value: number, unit: string } | null}
   */
  #rateMinWeight(rate) {
    return rate?.min_weight || rate?.dolny_prog_wagi || null;
  }

  /**
   * @param {any} rate
   * @returns {{ value: number, unit: string } | null}
   */
  #rateMaxWeight(rate) {
    return rate?.max_weight || rate?.gorny_prog_wagi || null;
  }

  /**
   * @param {any} rate
   * @returns {{ amount: string | number, currency_code: string } | null}
   */
  #ratePrice(rate) {
    return rate?.price || rate?.cena_wysylki || null;
  }

  /**
   * Converts a weight object to grams.
   * @param {{ value: number, unit: string } | null} weight
   * @param {number} [fallback]
   * @returns {number}
   */
  #weightToGrams(weight, fallback = Infinity) {
    if (!weight || weight.value == null) return fallback;
    const unit = String(weight.unit || "").toLowerCase();
    if (unit === "kg" || unit === "kilograms") return weight.value * 1000;
    return weight.value;
  }

  /**
   * Formats a shipping price with currency symbol.
   * @param {number} amount
   * @param {string} currency
   * @returns {string}
   */
  #formatShippingPrice(amount, currency) {
    const symbol = SHIPPING_CURRENCY_SYMBOLS[currency] || currency;
    let locale = "pl-PL";
    if (window.Shopify?.locale) {
      locale =
        SHIPPING_LOCALE_MAP[window.Shopify.locale] || window.Shopify.locale;
    }
    return `${amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
